import { apiClient } from "./client";
import endpoint from "@/constants/endpoint";
import type { StaffRoleType } from "@/constants";

export type UserStatus = "active" | "inactive" | "disabled";

// ─── Frontend types (camelCase) ───────────────────────────────────────────────

export interface AdminUser {
  id: string;
  name: string;
  role: StaffRoleType;
  status: UserStatus;
  /** ISO datetime of the user's last activity. */
  lastActivityAt: string;
}

export interface AdminUserDetail extends AdminUser {
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  languagePreference: string;
  addedByName: string;
  addedByRole: StaffRoleType;
  /** ISO datetime when the user was created. */
  dateAdded: string;
  /** ISO datetime of the user's last activity (for "Last Modified" display). */
  lastModified: string;
  profileImageUrl: string | null;
}

export interface UserActivityEntry {
  id: string;
  action: string;
  actorName: string;
  actorRole: string;
  details: Record<string, unknown> | null;
  createdAt: string;
  /** Human-readable description of the action (derived from action + details). */
  description: string;
  /** From-role when the action was a role change. */
  fromRole?: StaffRoleType;
  /** To-role when the action was a role change. */
  toRole?: StaffRoleType;
}

export interface UserActivityLogResponse {
  items: UserActivityEntry[];
}

export interface UsersListResponse {
  items: AdminUser[];
  total: number;
  activeCount: number;
  inactiveCount: number;
  disabledCount: number;
  /** Derived from backend offset/limit for UI pagination compatibility. */
  page: number;
  pageSize: number;
}

export interface AdminStats {
  applications: {
    total: number;
    pendingReview: number;
    approved: number;
    rejected: number;
  };
  invitations: {
    totalSent: number;
    new: number;
    resent: number;
  };
}

export interface CreateUserPayload {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  dialCode?: string;
  role: StaffRoleType;
  languagePreference?: "en" | "zh-CN" | "ar";
}

export interface UpdateUserPayload {
  firstName?: string;
  lastName?: string;
  phone?: string;
  /** Dial code is UI-only (combined with phone in display); not forwarded to backend. */
  dialCode?: string;
  role?: StaffRoleType;
  status?: UserStatus;
  languagePreference?: "en" | "zh-CN" | "ar";
  profileImageUrl?: string;
}

// ─── Backend raw types (snake_case) ───────────────────────────────────────────

interface BackendAdminUserItem {
  id: string;
  name: string;
  role: string;
  status: UserStatus;
  last_activity: string;
  last_activity_date: string;
  last_activity_time: string;
}

interface BackendAdminUserDetail extends BackendAdminUserItem {
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  language_preference: string;
  added_by_name: string;
  added_by_role: string;
  date_added: string;
  profile_image_url: string | null;
}

interface BackendAdminUserActivityEntry {
  id: string;
  action: string;
  actor_name: string;
  actor_role: string;
  details: Record<string, unknown> | null;
  created_at: string;
}

interface BackendAdminUserActivityLogResponse {
  items: BackendAdminUserActivityEntry[];
}

interface BackendUsersListResponse {
  items: BackendAdminUserItem[];
  total: number;
  active_count: number;
  inactive_count: number;
  disabled_count: number;
  offset: number;
  limit: number;
}

interface BackendAdminStats {
  applications: {
    total: number;
    pending_review: number;
    approved: number;
    rejected: number;
  };
  invitations: {
    total_sent: number;
    new: number;
    resent: number;
  };
}

// ─── Backend → frontend transformers ─────────────────────────────────────────

function toAdminUser(raw: BackendAdminUserItem): AdminUser {
  const lastActivityAt =
    raw.last_activity_date && raw.last_activity_time
      ? `${raw.last_activity_date}T${raw.last_activity_time}`
      : "";
  return {
    id: raw.id,
    name: raw.name,
    role: raw.role as StaffRoleType,
    status: raw.status,
    lastActivityAt,
  };
}

function toAdminUserDetail(raw: BackendAdminUserDetail): AdminUserDetail {
  const lastActivityIso =
    raw.last_activity_date && raw.last_activity_time
      ? `${raw.last_activity_date}T${raw.last_activity_time}`
      : "";
  return {
    ...toAdminUser(raw),
    firstName: raw.first_name,
    lastName: raw.last_name,
    email: raw.email,
    phone: raw.phone,
    languagePreference: raw.language_preference,
    addedByName: raw.added_by_name,
    addedByRole: raw.added_by_role as StaffRoleType,
    dateAdded: raw.date_added,
    lastModified: lastActivityIso,
    profileImageUrl: raw.profile_image_url,
  };
}

function toActivityEntry(raw: BackendAdminUserActivityEntry): UserActivityEntry {
  const details = raw.details ?? {};
  return {
    id: raw.id,
    action: raw.action,
    actorName: raw.actor_name,
    actorRole: raw.actor_role,
    details: raw.details,
    createdAt: raw.created_at,
    description: (details["description"] as string | undefined) ?? raw.action,
    fromRole: (details["from_role"] as StaffRoleType | undefined) ?? undefined,
    toRole: (details["to_role"] as StaffRoleType | undefined) ?? undefined,
  };
}

function toAdminStats(raw: BackendAdminStats): AdminStats {
  return {
    applications: {
      total: raw.applications.total,
      pendingReview: raw.applications.pending_review,
      approved: raw.applications.approved,
      rejected: raw.applications.rejected,
    },
    invitations: {
      totalSent: raw.invitations.total_sent,
      new: raw.invitations.new,
      resent: raw.invitations.resent,
    },
  };
}

// ─── Request builders ─────────────────────────────────────────────────────────

interface CreateAdminUserRequest {
  first_name: string;
  last_name: string;
  email: string;
  role: StaffRoleType;
  phone: string;
  language_preference: "en" | "zh-CN" | "ar";
}

function buildCreateAdminUserRequest(payload: CreateUserPayload): CreateAdminUserRequest {
  const firstName = payload.firstName?.trim();
  const lastName = payload.lastName?.trim();

  if (!firstName || !lastName) {
    throw new Error("Creating an admin user requires firstName and lastName.");
  }

  const email = payload.email.trim();
  if (!email) {
    throw new Error("Creating an admin user requires a valid email.");
  }

  // Combine dial code + local number into E.164-style: +2348162300796
  // Strip a leading zero from the local number when a dial code is present
  // (e.g. +234 + 08162300796 → +2348162300796, not +23408162300796).
  const localPhone = payload.phone?.trim() ?? "";
  const dialCode = payload.dialCode?.trim() ?? "";
  const normalised = dialCode && localPhone.startsWith("0") ? localPhone.slice(1) : localPhone;
  const phone = dialCode && normalised ? `${dialCode}${normalised}` : normalised;
  if (!phone) {
    throw new Error("Creating an admin user requires a phone number.");
  }

  return {
    first_name: firstName,
    last_name: lastName,
    email,
    role: payload.role,
    phone,
    language_preference: payload.languagePreference ?? "en",
  };
}

interface UpdateAdminUserRequest {
  first_name?: string;
  last_name?: string;
  phone?: string;
  role?: StaffRoleType;
  status?: UserStatus;
  language_preference?: "en" | "zh-CN" | "ar";
  profile_image_url?: string;
}

function buildUpdateAdminUserRequest(payload: UpdateUserPayload): UpdateAdminUserRequest {
  const req: UpdateAdminUserRequest = {};
  if (payload.firstName !== undefined) req.first_name = payload.firstName.trim();
  if (payload.lastName !== undefined) req.last_name = payload.lastName.trim();
  if (payload.phone !== undefined) req.phone = payload.phone;
  if (payload.role !== undefined) req.role = payload.role;
  if (payload.status !== undefined) req.status = payload.status;
  if (payload.languagePreference !== undefined) req.language_preference = payload.languagePreference;
  if (payload.profileImageUrl !== undefined) req.profile_image_url = payload.profileImageUrl;
  return req;
}

// ─── API module ───────────────────────────────────────────────────────────────

export const usersApi = {
  async list(params?: {
    tab?: "active" | "inactive" | "disabled";
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<UsersListResponse> {
    const pageSize = params?.limit ?? 20;
    const page = params?.page ?? 1;
    const offset = (page - 1) * pageSize;

    const qs = new URLSearchParams();
    qs.set("offset", String(offset));
    qs.set("limit", String(pageSize));
    if (params?.tab) qs.set("status", params.tab);
    if (params?.search) qs.set("search", params.search);

    const path = `${endpoint.adminUsers.list}?${qs.toString()}`;
    const res = await apiClient.get<BackendUsersListResponse>(path);
    const raw = res.data;

    return {
      items: raw.items.map(toAdminUser),
      total: raw.total,
      activeCount: raw.active_count,
      inactiveCount: raw.inactive_count,
      disabledCount: raw.disabled_count,
      page: Math.floor(raw.offset / raw.limit) + 1,
      pageSize: raw.limit,
    };
  },

  async getById(id: string): Promise<AdminUserDetail> {
    const res = await apiClient.get<BackendAdminUserDetail>(endpoint.adminUsers.detail(id));
    return toAdminUserDetail(res.data);
  },

  async activityLog(id: string): Promise<UserActivityLogResponse> {
    const res = await apiClient.get<BackendAdminUserActivityLogResponse>(
      endpoint.adminUsers.activityLog(id)
    );
    return { items: res.data.items.map(toActivityEntry) };
  },

  async createUser(payload: CreateUserPayload): Promise<AdminUserDetail> {
    const res = await apiClient.post<BackendAdminUserDetail>(
      endpoint.adminUsers.create,
      buildCreateAdminUserRequest(payload)
    );
    return toAdminUserDetail(res.data);
  },

  async updateUser(id: string, payload: UpdateUserPayload): Promise<AdminUserDetail> {
    const res = await apiClient.put<BackendAdminUserDetail>(
      endpoint.adminUsers.update(id),
      buildUpdateAdminUserRequest(payload)
    );
    return toAdminUserDetail(res.data);
  },

  async deactivateUser(id: string): Promise<AdminUserDetail> {
    const res = await apiClient.patch<BackendAdminUserDetail>(endpoint.adminUsers.deactivate(id), {});
    return toAdminUserDetail(res.data);
  },

  async reactivateUser(id: string): Promise<AdminUserDetail> {
    const res = await apiClient.patch<BackendAdminUserDetail>(endpoint.adminUsers.reactivate(id), {});
    return toAdminUserDetail(res.data);
  },

  async getStats(): Promise<AdminStats> {
    const res = await apiClient.get<BackendAdminStats>(endpoint.adminStats);
    return toAdminStats(res.data);
  },
};
